import {FC, useEffect, useRef} from "react";
import {Box, Tabs, Tab, Breadcrumbs, Typography, Button, CircularProgress} from "@mui/material";
import {Link, Route, Routes, Navigate, useLocation, useNavigate} from "react-router-dom";
import {makeStyles} from "tss-react/mui";
import {useTranslation} from "react-i18next";
import type {CourseDetailsDto, GetCourseCurriculumResponse} from "@/app2/shared/data";
import {CourseOverviewTab} from "./components/CourseOverviewTab";
import {CourseLessonsTab} from "./components/CourseLessonsTab";
import {useCourseEnrollment} from "./hooks/useCourseEnrollment";
import {bannerHeight, palette} from "@/constants";

interface StyleProps {
	hasBackgroundImage?: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme, {hasBackgroundImage}) => ({
	courseLibraryLandingPage: {
		marginBottom: "40px",
		"& th": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		},
		"& [class*=\"lessonsAdminTable\"]": {
			border: 0
		}
	},
	breadcrumbs: {
		marginBottom: "25px"
	},
	banner: {
		position: "relative",
		display: "flex",
		width: "100%",
		height: hasBackgroundImage ? bannerHeight : "auto",
		borderRadius: "5px",
		overflow: "hidden",
		marginBottom: "12px"
	},
	bannerTitle: {
		position: "relative",
		fontSize: "34px",
		lineHeight: "41px",
		fontWeight: "600",
		letterSpacing: "0.25px",
		alignSelf: "end",
		margin: "32px 20px 20px",
		zIndex: 2,
		[theme.breakpoints.down("sm")]: {
			margin: "32px 20px 10px",
			alignSelf: "flex-start"
		}
	},
	backgroundOverlay: {
		"&:after": {
			content: "''",
			position: "absolute",
			bottom: 0,
			left: 0,
			width: "100%",
			height: "100%",
			background: "linear-gradient(to top, #121212 0%, transparent 100%)",
			zIndex: 1
		}
	},
	titleRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		alignSelf: "flex-end",
		width: "100%",
		paddingRight: theme.spacing(3),
		zIndex: 2,
		[theme.breakpoints.down("sm")]: {
			flexDirection: "column",
			alignItems: "flex-start",
			justifyContent: "flex-start"
		}
	},
	backgroundImage: {
		position: "absolute",
		left: 0,
		top: 0,
		width: "100%",
		height: "100%",
		objectFit: "cover"
	},
	tabs: {
		borderBottom: 0,
		"& [class*=indicator]": {
			bottom: "4px",
			height: "2px"
		}
	},
	tab: {
		fontSize: "14px",
		fontWeight: "600",
		minHeight: "40px",
		minWidth: "85px",
		padding: 0,
		paddingLeft: "15px",
		paddingRight: "15px",
		marginLeft: 0,
		letterSpacing: "0.4px",
		textTransform: "uppercase",
		"&.Mui-selected": {
			color: palette.orange,
			background: "transparent",
			"& svg path": {
				fill: palette.orange
			}
		}
	},
	enrollButton: {
		minWidth: "120px",
		[theme.breakpoints.down("sm")]: {
			marginTop: "10px",
			marginLeft: "20px",
			marginBottom: "15px"
		}
	}
}));

interface CourseLibraryLandingPageProps {
	course: CourseDetailsDto;
	curriculum?: GetCourseCurriculumResponse;
	courseSlug: string;
	isLoadingCurriculum: boolean;
}

/**
 * Course Library Landing Page
 * 
 * Presentation component for the course library product page.
 * Shows course details, tabs (Overview/Lessons), and enrollment CTA.
 * Mirrors the structure and design of the old CourseLibraryLandingPage.
 */
export const CourseLibraryLandingPage: FC<CourseLibraryLandingPageProps> = ({
	course,
	curriculum,
	courseSlug,
	isLoadingCurriculum
}) => {
	const backgroundImageUrl = course.thumbnailUrl;
	const hasBackgroundImage = !!backgroundImageUrl;
	const {classes} = useStyles({hasBackgroundImage});
	const {t} = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const {enrollInCourse, isEnrolling} = useCourseEnrollment(courseSlug, course.id);
	const hasNavigated = useRef(false);

	const isEnrolled = !!course.enrollment;

	// Redirect to authenticated course page if already enrolled (after enrollment completes)
	useEffect(() => {
		if (isEnrolled && !hasNavigated.current) {
			hasNavigated.current = true;

			// Small delay to show success message
			setTimeout(() => {
				navigate(`/course/${courseSlug}`);
			}, 1500);
		}
	}, [isEnrolled, courseSlug, navigate]);

	const tabRoutes = [
		`/course-library/${courseSlug}/overview`,
		`/course-library/${courseSlug}/lessons`
	];

	const currentTab = tabRoutes.findIndex((route) => location.pathname === route);

	const handleTabChange = (_: React.SyntheticEvent, newTab: number): void => {
		navigate(tabRoutes[newTab]);
	};

	return (
		<Box className={classes.courseLibraryLandingPage}>
			{/* Breadcrumbs */}
			<Breadcrumbs className={classes.breadcrumbs} separator="/" aria-label="breadcrumb">
				<Link to="/course-library">
					<Typography variant="body1">{t("course_library_breadcrumb_courses")}</Typography>
				</Link>
				<Link to={`/course-library/${courseSlug}`}>
					<Typography variant="body1">{course.title}</Typography>
				</Link>
			</Breadcrumbs>

			{/* Banner with Background Image */}
			<Box className={classes.banner}>
				{backgroundImageUrl && (
					<Box className={classes.backgroundOverlay}>
						<img className={classes.backgroundImage} src={backgroundImageUrl} alt={course.title} />
					</Box>
				)}
				<Box className={classes.titleRow}>
					<Typography variant="h3" className={classes.bannerTitle}>
						{course.title}
					</Typography>
					<Button
						className={classes.enrollButton}
						onClick={enrollInCourse}
						variant="outlined"
						disabled={isEnrolling}
					>
						{isEnrolling ? <CircularProgress size={20} /> : t("course_library_enroll_button")}
					</Button>
				</Box>
			</Box>

			{/* Tabs */}
			{currentTab !== -1 && (
				<Tabs
					className={classes.tabs}
					value={currentTab}
					onChange={handleTabChange}
					aria-label="course tabs"
				>
					<Tab className={classes.tab} label={t("course_library_overview_tab")} />
					<Tab className={classes.tab} label={t("course_library_lessons_tab")} />
				</Tabs>
			)}

			{/* Tab Routes */}
			<Routes>
				<Route path="/" element={<Navigate to="overview" replace />} />
				<Route
					path="overview"
					element={<CourseOverviewTab course={course} curriculum={curriculum} />}
				/>
				<Route
					path="lessons"
					element={<CourseLessonsTab course={course} curriculum={curriculum} isLoading={isLoadingCurriculum} />}
				/>
			</Routes>
		</Box>
	);
};
